import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"

// Step 7 (onboarding/business_info) Continue-button gate. Disables "Finish
// Setup" until every required field is complete:
//   - home address (street, city, state, zip)
//   - business address (street, city, state, zip) OR "same as home" checked
//   - time zone, IMO, agency
//
// It also disables the "same as home" checkbox until a home address exists,
// since there's nothing to copy otherwise.
//
// Agency lock/unlock (disabled state, placeholder, clear-on-IMO-change) is
// owned by the `imo-agency` controller rendered on the same step. This
// controller only reads the agency field's value to decide whether the
// Continue button can be enabled.
//
// Listens for input/change on the root so it reacts to manual typing, <select>
// changes, the IMO/agency autocomplete inputs, AND Google Places autofill (the
// address-autocomplete controller dispatches `change` after filling, since a
// programmatic .value assignment fires no native event).
// OnboardingStepService#save_business_info enforces the same fields server-side.
export default class extends Controller {
  static targets = ["continueBtn", "statusMessage"]

  static HOME = [
    "home_address_street",
    "home_address_city",
    "home_address_state",
    "home_address_zip",
  ]

  static BUSINESS = [
    "business_address_street",
    "business_address_city",
    "business_address_state",
    "business_address_zip",
  ]

  // IMO/agency names must be at least 3 characters (matches the server-side
  // minimum); every other required field just needs a non-empty value.
  static MIN_LENGTH = { imo: 3, agency: 3 }

  connect() {
    this._onChange = () => this.refresh()
    this.element.addEventListener("input", this._onChange)
    this.element.addEventListener("change", this._onChange)
    this.refresh()
  }

  disconnect() {
    this.element.removeEventListener("input", this._onChange)
    this.element.removeEventListener("change", this._onChange)
  }

  fieldFilled(name) {
    const field = this.element.querySelector(`[name="${name}"]`)
    if (!field) return false
    const min = this.constructor.MIN_LENGTH[name] || 1
    return field.value.trim().length >= min
  }

  sameAsHome() {
    // The checkbox itself, never the switch's hidden '0' of the same name (#16504).
    const box = this.element.querySelector('input[type="checkbox"][name="same_as_home_address"]')
    return box ? box.checked : false
  }

  // Returns the human-readable labels of the requirement groups still missing,
  // so the status banner names exactly what's left instead of repeating the
  // full list after the user has already filled some of it.
  missing() {
    const homeReady = this.constructor.HOME.every((name) => this.fieldFilled(name))
    const businessReady = this.sameAsHome()
      ? homeReady
      : this.constructor.BUSINESS.every((name) => this.fieldFilled(name))

    // Ordered to match the on-screen field order: home, business, IMO,
    // agency, time zone.
    const labels = []
    if (!homeReady) labels.push("home address")
    if (!businessReady) labels.push("business address")
    if (!this.fieldFilled("imo")) labels.push("IMO")
    if (!this.fieldFilled("agency")) labels.push("agency")
    if (!this.fieldFilled("time_zone")) labels.push("time zone")
    return { labels, homeReady }
  }

  refresh() {
    const { labels, homeReady } = this.missing()
    const ready = labels.length === 0

    // "Same as home" is meaningless without a home address to copy.
    const sameBox = this.element.querySelector('input[type="checkbox"][name="same_as_home_address"]')
    if (sameBox) sameBox.disabled = !homeReady

    const message = ready ? "" : `Add your ${this.joinLabels(labels)} to continue.`

    if (this.hasContinueBtnTarget) {
      syncGateReason(this.continueBtnTarget, { ready, reason: message })
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = message
      this.statusMessageTarget.classList.toggle("d-none", ready)
    }
  }

  // "a" / "a and b" / "a, b, and c"
  joinLabels(labels) {
    if (labels.length === 1) return labels[0]
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
    return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`
  }
}
